import React from 'react';
import LegalLayout, {
    Section,
    SubTitle,
    Bullets,
    Strong,
    Mail,
} from '@/components/legal/LegalLayout';

export default function PrivacyPolicy() {
    return (
        <LegalLayout title="Privacy Policy" updated="June 20, 2026">
            <Section n={1} title="Who We Are">
                <p>
                    HyperTroop Fitness Tracker ("HyperTroop", "we", "our") is
                    operated by an individual developer based in India. You can
                    reach us at <Mail>support@hypertroop.fit</Mail>.
                </p>
            </Section>

            <Section n={2} title="Who Can Use This App">
                <p>
                    HyperTroop is intended for users{' '}
                    <Strong>18 years of age or older</Strong>. By creating an
                    account, you confirm that you are at least 18 years old. We
                    do not knowingly collect data from anyone under 18. If we
                    become aware that a user is under 18, we will delete their
                    account and data promptly.
                </p>
            </Section>

            <Section n={3} title="What Data We Collect">
                <p>We collect only the data you directly provide to us:</p>

                <SubTitle>Account data</SubTitle>
                <Bullets
                    items={[
                        'Full name, email address, and password (stored as a hashed credential)',
                    ]}
                />

                <SubTitle>Profile data</SubTitle>
                <Bullets
                    items={[
                        'Date of birth, biological sex, height, weight, body fat percentage, activity level, and fitness goal — all entered by you in the Profile screen',
                    ]}
                />

                <SubTitle>Body measurements</SubTitle>
                <Bullets
                    items={[
                        'Body weight and circumference measurements (neck, chest, arms, waist, hips, thighs, calves) logged by you over time',
                    ]}
                />

                <SubTitle>Workout & training data</SubTitle>
                <Bullets
                    items={[
                        'Programs, workouts, and exercises you create or log',
                        'Progress entries (sets, reps, weight) you record per session',
                    ]}
                />

                <SubTitle>User-provided content</SubTitle>
                <Bullets
                    items={[
                        'Exercise instructions and YouTube URLs you optionally add to exercises',
                    ]}
                />

                <p>
                    We do <Strong>not</Strong> collect location data, device
                    identifiers, usage analytics, or any data beyond what you
                    explicitly enter.
                </p>
            </Section>

            <Section n={4} title="How We Use Your Data">
                <p>We use your data solely to operate the app:</p>
                <Bullets
                    items={[
                        'Authenticate your account and keep your session secure',
                        'Display your fitness stats, history, and progress charts',
                        'Calculate derived metrics (BMI, BMR, lean body mass) shown in the Profile screen',
                        'Enable features like personal records, activity streaks, and volume analytics',
                    ]}
                />
                <p>
                    We do <Strong>not</Strong> use your data for advertising,
                    profiling, or any purpose beyond providing the app's
                    features to you.
                </p>
            </Section>

            <Section n={5} title="How Your Data Is Stored">
                <p>
                    Your data is stored on <Strong>Supabase</Strong>, a cloud
                    database platform. Supabase encrypts data at rest and in
                    transit. We do not use any other third-party service to
                    store or process your personal data.
                </p>
            </Section>

            <Section n={6} title="Data Sharing">
                <p>
                    We do <Strong>not</Strong> sell, rent, or share your
                    personal data with any third party, except:
                </p>
                <Bullets
                    items={[
                        <>
                            <Strong>Supabase</Strong> — as our database
                            infrastructure provider, to the extent necessary to
                            store and retrieve your data
                        </>,
                        <>
                            <Strong>Legal obligation</Strong> — if required by
                            Indian law, court order, or a competent government
                            authority
                        </>,
                    ]}
                />
            </Section>

            <Section n={7} title="Data Retention and Deletion">
                <p>
                    Your data is retained for as long as your account is
                    active. You can permanently delete your account and all
                    associated data at any time from{' '}
                    <Strong>Settings → Delete Account</Strong>. Deletion is
                    immediate and irreversible.
                </p>
            </Section>

            <Section n={8} title="Your Rights">
                <p>
                    Under India's Digital Personal Data Protection Act, 2023
                    (DPDP Act), you have the right to:
                </p>
                <Bullets
                    items={[
                        <>
                            <Strong>Access</Strong> the personal data we hold
                            about you
                        </>,
                        <>
                            <Strong>Correct</Strong> inaccurate data (via the
                            Profile and Settings screens)
                        </>,
                        <>
                            <Strong>Erase</Strong> your data by deleting your
                            account
                        </>,
                        <>
                            <Strong>Withdraw consent</Strong> by deleting your
                            account at any time
                        </>,
                    ]}
                />
                <p>
                    To exercise any right not covered by in-app controls, email
                    us at <Mail>support@hypertroop.fit</Mail>.
                </p>
            </Section>

            <Section n={9} title="Security">
                <p>
                    We implement reasonable technical measures to protect your
                    data, including encrypted transmission (HTTPS) and
                    Supabase's built-in security controls. No system is
                    completely secure; you are responsible for keeping your
                    password confidential.
                </p>
            </Section>

            <Section n={10} title="Changes to This Policy">
                <p>
                    We may update this policy from time to time. When we do, we
                    will update the "Last updated" date at the top. Continued
                    use of the app after changes constitutes acceptance of the
                    revised policy.
                </p>
            </Section>

            <Section n={11} title="Contact">
                <p>
                    For privacy questions or requests, contact:{' '}
                    <Mail>support@hypertroop.fit</Mail>
                </p>
            </Section>
        </LegalLayout>
    );
}
